import { useLocalObservable } from 'mobx-react-lite';

import { useTranslation } from '@gem/modules/i18n';
import { ICommonPopupsProps } from '@gem/popups';
import { replaceHtmlTagsToSpecialSymbols } from '@gem/utils';

import Tasks from 'APP/Tasks';
import { GroupType } from 'APP/model/group/groupModel.types';
import { IShareSelection, TChangeSelectionHandler, TSelectItemHandler } from 'APP/types/share';
import Entities from 'STORE';
import { TextPayload } from 'STORE/Messages/Message/Payload/TextPayload/TextPayload';

export interface IForwardMessageWithInfoPopupParams {
  title: string;
  info: string;
  showToastCallback?: () => void;
}

export interface IForwardIMessageWithInfoPopupPresenter {
  selection: IShareSelection[];
  isSubmitting: boolean;
  hasSelected: boolean;
  canForwardMessage(data: IShareSelection): boolean;
  onClose(): void;
  onSelectItem: TSelectItemHandler;
  onChangeSelection: TChangeSelectionHandler;
  submit(comment: string): Promise<void>;
  send(targetGroupIds: string[], comment: string): Promise<void>;
}

const CHECKING_PERMISSION_GROUPS_TYPES = [
  GroupType.Closed,
  GroupType.Open,
  GroupType.ChannelClosed,
  GroupType.ChannelOpen,
];

export function useForwardMessageWithInfoPresenter({
  params,
  popupInstance,
  onClose,
}: ICommonPopupsProps<IForwardMessageWithInfoPopupParams>): IForwardIMessageWithInfoPopupPresenter {
  const { t } = useTranslation();

  const presenter = useLocalObservable<IForwardIMessageWithInfoPopupPresenter>(() => ({
    isSubmitting: false,
    selection: [],

    get hasSelected(): boolean {
      return presenter.selection.length > 0;
    },

    canForwardMessage(data: IShareSelection): boolean {
      if (!data.data.type || !CHECKING_PERMISSION_GROUPS_TYPES.includes(data.data.type)) {
        return true;
      }

      const targetGroup = Entities.GroupsStore.getGroupById(data.data.id);

      if (!targetGroup || !targetGroup.canSendMessages) {
        return false;
      }

      return true;
    },

    onClose(): void {
      popupInstance.close();
      onClose?.();
    },

    onSelectItem(data: IShareSelection): boolean {
      if (!presenter.canForwardMessage(data)) {
        Tasks.app.showToast(t('moderation_content_forward', { 0: data.data.avatarTitle }));
        return false;
      }

      return true;
    },

    onChangeSelection(data: IShareSelection[]): void {
      presenter.selection = data;
    },

    async submit(comment: string): Promise<void> {
      presenter.isSubmitting = true;

      const selection = presenter.selection.filter((data) => presenter.canForwardMessage(data));

      const targetGroupIds = await Tasks.forward.getTargetGroupIds(selection);

      presenter.send(targetGroupIds, comment);

      presenter.onClose();
    },

    async send(targetGroupIds: string[], comment: string): Promise<void> {
      let sendError = '';

      for (let i = 0; i < targetGroupIds.length; i++) {
        const groupId = targetGroupIds[i];

        const payload = new TextPayload({
          text: replaceHtmlTagsToSpecialSymbols(
            comment ? `${comment} <div>${params?.info}</div>` : params?.info
          ),
        });

        const canSend = Tasks.messaging.canSendMessage({ groupId, payload });
        if (!sendError && canSend.error && !canSend.result) {
          sendError = canSend.error;
          break;
        }

        await Tasks.messaging.createNewMessages(groupId, [{ payload }]);
      }

      if (sendError) {
        Tasks.app.showToast(sendError);
      }

      params?.showToastCallback?.();
    },
  }));

  return presenter;
}
